import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../../store/authStore';
import { useTranslation } from 'react-i18next';
import { signInWithGoogle, renderGoogleButton } from '../../services/googleAuth';

interface LoginForm {
  username: string;
  password: string;
}

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || searchParams.get('returnUrl');
  const { login, loginWithGoogle } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>();

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    try {
      await login(data.username, data.password);
      toast.success(t('auth.welcomeBack'));
      // Redirect to the original destination or home
      navigate(redirectTo || '/');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || t('auth.signIn'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    try {
      const idToken = await signInWithGoogle();
      await loginWithGoogle(idToken);
      toast.success(t('auth.welcomeBack'));
      navigate(redirectTo || '/');
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Google sign-in failed';
      toast.error(errorMessage);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  useEffect(() => {
    // Render Google button if available
    const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (googleButtonRef.current && googleClientId) {
      // Clear any existing content
      googleButtonRef.current.innerHTML = '';
      
      // Small delay to ensure Google script is fully loaded
      const timer = setTimeout(() => {
        renderGoogleButton(googleButtonRef.current!, handleGoogleLogin, {
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
        }).catch((err) => {
          console.error('Failed to render Google button:', err);
          toast.error('Failed to load Google sign-in. Please refresh the page.');
        });
      }, 100);

      return () => clearTimeout(timer);
    } else if (!googleClientId) {
      console.warn('VITE_GOOGLE_CLIENT_ID is not set');
    }
  }, []);

  return (
    <div>
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">{t('auth.welcomeBack')}</h2>
        <p className="text-surface-400">
          {t('auth.signInSubtitle')}
        </p>
      </div>

      {/* Google Login - Primary */}
      {import.meta.env.VITE_GOOGLE_CLIENT_ID && (
        <div className="mb-6">
          <div className="flex justify-center">
            <div ref={googleButtonRef} id="google-signin-button" className="min-h-[40px]"></div>
          </div>
        </div>
      )}

      {/* Regular Login - Secondary */}
      {import.meta.env.VITE_GOOGLE_CLIENT_ID && (
        <div className="mb-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-surface-700"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-surface-900 text-surface-400">
                {t('auth.orSignInWithEmail', 'Or sign in with email')}
              </span>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div className="input-group">
          <label className="input-label">{t('auth.usernameOrEmail')}</label>
          <input
            type="text"
            className="input"
            placeholder={t('auth.usernameOrEmail')}
            {...register('username', { required: t('auth.usernameOrEmail') })}
          />
          {errors.username && (
            <p className="text-sm text-red-400">{errors.username.message}</p>
          )}
        </div>

        <div className="input-group">
          <label className="input-label">{t('auth.password')}</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              className="input pr-12"
            placeholder={t('auth.password')}
            {...register('password', { required: t('auth.password') })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400 hover:text-white transition-colors"
            >
              {showPassword ? (
                <EyeSlashIcon className="w-5 h-5" />
              ) : (
                <EyeIcon className="w-5 h-5" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="text-sm text-red-400">{errors.password.message}</p>
          )}
        </div>

        <motion.button
          type="submit"
          disabled={isLoading || isGoogleLoading}
          className="btn-primary w-full"
          whileTap={{ scale: 0.98 }}
        >
          {isLoading ? (
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>{t('auth.signIn')}</span>
            </div>
          ) : (
            t('auth.signIn')
          )}
        </motion.button>
      </form>

      <div className="mt-6 text-center">
        <p className="text-surface-400">
          {t('auth.noAccount')}{' '}
          <Link
            to={redirectTo ? `/register?redirect=${encodeURIComponent(redirectTo)}` : '/register'}
            className="text-primary-400 hover:text-primary-300 font-medium"
          >
            {t('auth.createOne')}
          </Link>
        </p>
      </div>
    </div>
  );
}

